import { Button } from '@workspace/ui/components/Button';
import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import { IconButton } from '~/components/isolated/IconButton';
import { LanguageList } from '~/components/LanguageSelector/LanguageList';
import { useLang } from '~/hooks/useLang';
import { usePushNotifications } from '~/hooks/usePushNotifications';
import { useStatusBarControl } from '~/hooks/useStatusBarControl';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { userStore } from '~/stores';

import { useOnboardingHandlers } from '../../hooks/useOnboardingHandlers';
import { useStepData } from '../../hooks/useStepData';

import notificationsImage from './assets/notifications.png';

export const MCosmeticOnboarding = () => {
  const { t } = useTranslation();
  const { setLanguage } = useLang();

  const {
    handleChangeScreenIndex,
    handleTimerAlive,
    currentStepData,
    currentStep,
    stepsNumber,
  } = useStepData();

  const { handleNextPress, handleLaterPress } = useOnboardingHandlers({
    currentStep,
    stepsNumber,
    handleChangeScreenIndex,
    handleTimerAlive,
  });

  const { requestNotificationPermission } = usePushNotifications();

  useStatusBarControl({
    initialColor: 'light-content',
  });

  const currentLang = userStore.lang || company.config.lang;
  const isNotificationsStep = currentStep === 1;

  const handleContinuePress = () => {
    if (isNotificationsStep) requestNotificationPermission();
    handleNextPress();
  };

  useEffect(() => {
    const dialogPayload = {
      type: 'no_store',
      postcode: userStore.deliveryAddress?.zip || '',
      lat: userStore.deliveryAddress?.coordinates.lat || '',
      lon: userStore.deliveryAddress?.coordinates.lng || '',
    };
    analyticsService.track(
      AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_SHOWN,
      dialogPayload,
    );
    analyticsService.track(
      AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_SHOWN_BI,
      dialogPayload,
    );
  }, []);

  return (
    <div
      style={{ backgroundImage: `url('${currentStepData?.img}')` }}
      className="fixed top-0 right-0 bottom-0 left-0 flex h-full w-full flex-col justify-between bg-center bg-cover pt-safe-offset-5"
    >
      <div className="grid grid-cols-3 px-4">
        <div className="col-span-1 col-start-2 flex h-10 flex-1 items-center justify-center gap-1">
          {Array.from({ length: stepsNumber }, (_, index) => (
            <div
              key={index}
              className={cn(
                'size-1.5 rounded-full',
                index === currentStep
                  ? 'bg-graphic-regular-white'
                  : 'bg-graphic-transparent-white-25',
              )}
            />
          ))}
        </div>
        <div className="col-span-1 flex justify-end">
          <Link to="/">
            <IconButton
              icon="close"
              variant="transparent"
              className="bg-surface-transparent-4! text-graphic-regular-white!"
            />
          </Link>
        </div>
      </div>

      {isNotificationsStep && (
        <img src={notificationsImage} alt="notifications" className="w-full" />
      )}

      <div className="flex flex-col gap-8 bg-[linear-gradient(180deg,transparent_0%,rgba(0,0,0,0.50)_25%,rgba(0,0,0,0.75)_100%)] px-4 pb-safe-offset-7">
        <div className="flex flex-col items-center gap-2">
          <Typography.Header200Semibold className="text-graphic-regular-white! text-center">
            {currentStepData?.textTitle}
          </Typography.Header200Semibold>
          <Typography.Text200Medium className="text-graphic-regular-white! text-center">
            {currentStepData?.textDescription}
          </Typography.Text200Medium>
        </div>

        {currentStep === 0 && (
          <LanguageList
            variant="light"
            currentLang={currentLang}
            onSelect={setLanguage}
            className="px-4 *:text-graphic-regular-white!"
          />
        )}

        <div className="flex flex-col gap-2">
          {currentStep > 0 && (
            <Button
              variant="primary"
              size="lg"
              onClick={handleLaterPress}
              className="border-none bg-graphic-transparent-white-14"
            >
              {t('skip')}
            </Button>
          )}
          <Button variant="brand" size="lg" onClick={handleContinuePress}>
            {t(isNotificationsStep ? 'allowNotifications' : 'continue')}
          </Button>
        </div>
      </div>
    </div>
  );
};
